// LockScreen.tsx
// 'use client';

import React, { useReducer, useCallback } from "react";
import "./Lockscreen.css";

interface LockScreenProps {
  unlock: () => void;
  password: string;
  className?: string;
}

interface State {
  passcode: number[];
  heading: string;
  isDisabled: boolean;
}

type Action =
  | { type: "SET_PASSCODE"; passcode: number[] }
  | { type: "SET_HEADING"; heading: string }
  | { type: "SET_DISABLED"; isDisabled: boolean };

function reducer(state: State, action: Action): State {
  switch (action.type) {
    case "SET_PASSCODE":
      return { ...state, passcode: action.passcode };
    case "SET_HEADING":
      return { ...state, heading: action.heading };
    case "SET_DISABLED":
      return { ...state, isDisabled: action.isDisabled };
    default:
      return state;
  }
}

const LockScreen: React.FC<LockScreenProps> = React.memo(
  ({ unlock, password, className }) => {
    const [state, dispatch] = useReducer(reducer, {
      passcode: [],
      heading: "Enter Passcode",
      isDisabled: false,
    });

    const handleButtonClick = useCallback(
      (digit: number) => {
        if (!state.isDisabled && state.passcode.length < 6) {
          dispatch({
            type: "SET_PASSCODE",
            passcode: [...state.passcode, digit],
          });
        }
      },
      [state.isDisabled, state.passcode]
    );

    const handleDelete = useCallback(() => {
      if (!state.isDisabled) {
        dispatch({
          type: "SET_PASSCODE",
          passcode: state.passcode.slice(0, -1),
        });
      }
    }, [state.isDisabled, state.passcode]);

    const handleSubmit = useCallback(
      (event: React.FormEvent) => {
        event.preventDefault();
        // Check if the passcode is correct
        if (state.passcode.join("") === password) {
          // Hide the lock screen and show the home page
          unlock();
        } else if (state.passcode.length === 6) {
          // Display toast message for incorrect password
          dispatch({ type: "SET_HEADING", heading: "Incorrect" });
          dispatch({ type: "SET_PASSCODE", passcode: [] });
          dispatch({ type: "SET_DISABLED", isDisabled: true });
          setTimeout(
            () => dispatch({ type: "SET_DISABLED", isDisabled: false }),
            1500
          );
        }
      },
      [state.passcode, unlock, password]
    );

    return (
      <div className={`${className}`}>
        <div className="lockscreen">
          <div className="time-display">
            {new Date().toLocaleTimeString([], {
              hour: "2-digit",
              minute: "2-digit",
            })}
          </div>
          <form onSubmit={handleSubmit} className="password-form">
            <div>
              <h1>{state.heading}</h1>
            </div>
            <div className="passcode-display">
              {Array.from({ length: 6 }, (_, i) => (
                <div
                  key={i}
                  className={`dot ${i < state.passcode.length ? "filled" : ""}`}
                ></div>
              ))}
            </div>
            <div className="number-pad">
              {Array.from({ length: 9 }, (_, i) => (
                <button key={i + 1} onClick={() => handleButtonClick(i + 1)}>
                  {i + 1}
                </button>
              ))}
              <div></div>
              <button onClick={() => handleButtonClick(0)}>0</button>
              <button onClick={handleDelete}>Del</button>
            </div>
          </form>
        </div>
      </div>
    );
  }
);

export default LockScreen;
